import useEmblaCarousel from "embla-carousel-react";
import {
  Children,
  useImperativeHandle,
  type FunctionComponent,
  type ReactNode,
  type RefObject,
} from "react";

import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";

import { ArrowButton } from "./ArrowButton";
import { DotNavigation } from "./DotNavigation";
import { Item } from "./Item";
import { SlideContainer } from "./SlideContainer";

interface Props {
  children: ReactNode;
  /**
   * Specifies whether or not the carousel items should be full width
   *
   * @default false
   */
  isFullWidth?: boolean;
  /**
   * Specifies whether to hide the navigation Arrows, and dot navigation in footer
   * This is to use the slides as more of an information stepper, using the useImperativeHandle for manual navigation
   * from external functions
   *
   * @default false
   */
  hideNavigation?: boolean;
  ref?: RefObject<CarouselHandle>;
}

// Expose the button functions to the outside
export interface CarouselHandle {
  next: () => void;
  previous: () => void;
}
/**
 * @deprecated DS4 component — a DS5 `Carousel` replacement is coming soon. Do not add new usages.
 *
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/display/NewCarousel/
 */
const CarouselComponent = ({
  children,
  isFullWidth = false,
  hideNavigation = false,
  ref,
}: Props) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", slidesToScroll: "auto" });

  const { phone, phoneLarge, desktop } = useBreakpoints();

  const allSlideItems = Children.toArray(children);

  // Always show only a max of 5 slides at all times
  const maxSlides = () => {
    switch (true) {
      // if items are full width
      case isFullWidth:
        return allSlideItems.slice(0, 5);
      // 600px or less
      case phoneLarge.lessThan:
        return allSlideItems.slice(0, 5);
      // 599px-1023px
      case phone.greaterThan && desktop.lessThan:
        return allSlideItems.slice(0, 10);
      default:
        return allSlideItems.slice(0, 20);
    }
  };

  useImperativeHandle(ref, () => ({
    next: () => emblaApi?.scrollNext(),
    previous: () => emblaApi?.scrollPrev(),
  }));

  return (
    <section>
      <NewCarousel.SlideContainer
        hideNavigation={hideNavigation}
        emblaRef={emblaRef}
        emblaApi={emblaApi}
        isFullWidth={isFullWidth}
      >
        {maxSlides()}
      </NewCarousel.SlideContainer>
      {allSlideItems.length > 1 && !hideNavigation && (
        <NewCarousel.DotNavigation emblaApi={emblaApi} />
      )}
    </section>
  );
};

const NewCarousel = CarouselComponent as FunctionComponent<Props> & {
  ArrowButton: typeof ArrowButton;
  DotNavigation: typeof DotNavigation;
  Item: typeof Item;
  SlideContainer: typeof SlideContainer;
};

// Attach static properties
NewCarousel.ArrowButton = ArrowButton;
NewCarousel.DotNavigation = DotNavigation;
NewCarousel.Item = Item;
NewCarousel.SlideContainer = SlideContainer;
export { NewCarousel };
